/**
 * SaasCardManager — manage the credit cards on file for a tenant's
 * SaaS subscription (platform Stripe account).
 *
 * Shared by two surfaces via the `basePath` prop:
 *   • Super Admin onboarding → basePath="/super-admin/tenants/{id}"
 *   • Tenant self-service     → basePath="/admin/business-settings"
 *
 * Rules (enforced by the backend, mirrored in the UI):
 *   • Credit cards only.
 *   • At least one card must always remain.
 *   • Exactly one card is "primary" (the one billed monthly).
 *   • The primary card can't be removed — swap primary first.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { toast } from 'sonner';
import {
  CreditCard,
  Loader2,
  Plus,
  ShieldCheck,
  Star,
  Trash2,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import api from '../../services/api';

const stripeLoaderCache = {};
function getStripePromise(pk) {
  if (!pk) return null;
  if (!stripeLoaderCache[pk]) stripeLoaderCache[pk] = loadStripe(pk);
  return stripeLoaderCache[pk];
}

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '15px',
      color: '#1e293b',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      '::placeholder': { color: '#94a3b8' },
    },
    invalid: { color: '#dc2626', iconColor: '#dc2626' },
  },
  hidePostalCode: false,
};

const brandLabel = (b) =>
  ({ visa: 'Visa', mastercard: 'Mastercard', amex: 'Amex', discover: 'Discover' }[
    String(b || '').toLowerCase()
  ] || (b ? b[0].toUpperCase() + b.slice(1) : 'Card'));

// ---- Add-card form (inside Stripe <Elements>) ---------------------------
function AddCardForm({ basePath, clientSecret, makePrimary, onDone, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);
  const [cardError, setCardError] = useState(null);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!stripe || !elements) return;
    setSubmitting(true);
    try {
      const result = await stripe.confirmCardSetup(clientSecret, {
        payment_method: { card: elements.getElement(CardElement) },
      });
      if (result.error) {
        const msg = result.error.message || 'Failed to save card';
        setCardError(msg);
        toast.error(msg, { duration: 7000 });
        return;
      }
      const pmId = result.setupIntent?.payment_method;
      if (!pmId) {
        toast.error('Card saved at Stripe but no PaymentMethod id returned — please reload.');
        return;
      }
      const { data } = await api.post(`${basePath}/payment-methods`, {
        payment_method_id: pmId,
        make_primary: !!makePrimary,
      });
      toast.success('Card added');
      onDone?.(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : err.message || 'Failed to add card');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="saas-add-card-form">
      <div>
        <Label className="text-xs font-medium text-slate-600 mb-1.5 block">Card details</Label>
        <div className="rounded-lg border border-slate-300 bg-white px-3 py-2.5 focus-within:border-slate-900 focus-within:ring-1 focus-within:ring-slate-900">
          <CardElement
            options={CARD_ELEMENT_OPTIONS}
            onChange={(ev) => {
              setCardComplete(ev.complete);
              setCardError(ev.error?.message || null);
            }}
          />
        </div>
        {cardError && (
          <p className="text-xs text-red-600 mt-1.5" data-testid="saas-card-error">{cardError}</p>
        )}
      </div>
      <DialogFooter className="gap-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={!stripe || !cardComplete || submitting}
          data-testid="saas-add-card-submit"
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
        >
          {submitting ? (
            <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Saving…</>
          ) : (
            <><ShieldCheck className="h-4 w-4 mr-1.5" /> Save Card</>
          )}
        </Button>
      </DialogFooter>
    </form>
  );
}

// ---- Main manager -------------------------------------------------------
export default function SaasCardManager({ basePath, title = 'Payment Methods', readOnly = false }) {
  const [state, setState] = useState({ loading: true, error: null, cards: [], primary: null });
  const [addOpen, setAddOpen] = useState(false);
  const [setup, setSetup] = useState(null);
  const [preparing, setPreparing] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const refresh = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const { data } = await api.get(`${basePath}/payment-methods`);
      setState({
        loading: false, error: null,
        cards: data.payment_methods || [],
        primary: data.primary_payment_method_id || null,
      });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setState({ loading: false, error: typeof detail === 'string' ? detail : 'Failed to load cards', cards: [], primary: null });
    }
  }, [basePath]);

  useEffect(() => { refresh(); }, [refresh]);

  const openAdd = async () => {
    setPreparing(true);
    setAddOpen(true);
    setSetup(null);
    try {
      const { data } = await api.post(`${basePath}/payment-methods/setup-intent`, {});
      setSetup(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not start card entry');
      setAddOpen(false);
    } finally {
      setPreparing(false);
    }
  };

  const setPrimary = async (pmId) => {
    setBusyId(pmId);
    try {
      const { data } = await api.put(`${basePath}/payment-methods/${pmId}/primary`);
      setState((s) => ({ ...s, cards: data.payment_methods || [], primary: data.primary_payment_method_id }));
      toast.success('Primary card updated');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to set primary');
    } finally {
      setBusyId(null);
    }
  };

  const removeCard = async (pmId) => {
    setBusyId(pmId);
    try {
      const { data } = await api.delete(`${basePath}/payment-methods/${pmId}`);
      setState((s) => ({ ...s, cards: data.payment_methods || [], primary: data.primary_payment_method_id }));
      toast.success('Card removed');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to remove card');
    } finally {
      setBusyId(null);
    }
  };

  const stripePromise = useMemo(() => getStripePromise(setup?.publishable_key), [setup?.publishable_key]);
  const hasNoCards = !state.loading && state.cards.length === 0;

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5 space-y-4" data-testid="saas-card-manager">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-slate-900 flex items-center gap-2">
          <CreditCard className="h-5 w-5 text-blue-600" /> {title}
        </h3>
        {!readOnly && (
          <Button size="sm" onClick={openAdd} data-testid="saas-add-card-btn"
                  className="bg-blue-600 hover:bg-blue-700 text-white">
            <Plus className="h-4 w-4 mr-1.5" /> Add card
          </Button>
        )}
      </div>

      {state.loading && (
        <div className="flex items-center gap-2 text-sm text-slate-500 py-4" data-testid="saas-cards-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading cards…
        </div>
      )}

      {state.error && !state.loading && (
        <div className="text-sm text-red-600" data-testid="saas-cards-error">{state.error}</div>
      )}

      {hasNoCards && !state.error && (
        <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600"
             data-testid="saas-cards-empty">
          No card on file yet. {readOnly ? 'Your platform admin will add one.' : 'Add a credit card to enable automatic monthly billing.'}
        </div>
      )}

      {!state.loading && state.cards.length > 0 && (
        <ul className="divide-y divide-slate-100" data-testid="saas-cards-list">
          {state.cards.map((c) => (
            <li key={c.id} className="flex items-center justify-between py-3" data-testid={`saas-card-row-${c.id}`}>
              <div className="flex items-center gap-3">
                <div className="h-9 w-12 rounded bg-slate-100 border border-slate-200 flex items-center justify-center text-[10px] font-semibold text-slate-600">
                  {brandLabel(c.brand)}
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900">
                    {brandLabel(c.brand)} •••• {c.last4}
                    {c.is_primary && (
                      <Badge className="ml-2 bg-amber-100 text-amber-800 border-amber-200" data-testid={`saas-card-primary-badge-${c.id}`}>
                        <Star className="h-3 w-3 mr-1 inline-block fill-amber-500 text-amber-500" /> Primary
                      </Badge>
                    )}
                  </p>
                  <p className="text-xs text-slate-500">
                    Expires {String(c.exp_month).padStart(2, '0')}/{c.exp_year}
                  </p>
                </div>
              </div>
              {!readOnly && (
                <div className="flex items-center gap-2">
                  {!c.is_primary && (
                    <Button size="sm" variant="outline" disabled={busyId === c.id}
                            onClick={() => setPrimary(c.id)} data-testid={`saas-card-make-primary-${c.id}`}>
                      {busyId === c.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Star className="h-3.5 w-3.5 mr-1" /> Make primary</>}
                    </Button>
                  )}
                  <Button size="sm" variant="ghost"
                          disabled={busyId === c.id || c.is_primary || state.cards.length <= 1}
                          onClick={() => removeCard(c.id)}
                          title={c.is_primary ? 'Set another card as primary first' : (state.cards.length <= 1 ? 'At least one card must remain' : 'Remove card')}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`saas-card-remove-${c.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Add-card dialog */}
      <Dialog open={addOpen} onOpenChange={(v) => { setAddOpen(v); if (!v) setSetup(null); }}>
        <DialogContent className="max-w-md" data-testid="saas-add-card-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-blue-600" /> Add a credit card
            </DialogTitle>
            <DialogDescription>
              {hasNoCards ? 'This first card becomes your primary billing card.' : 'Saved securely with Stripe. You can make it primary or keep it as a backup.'}
            </DialogDescription>
          </DialogHeader>
          {preparing && (
            <div className="py-8 flex flex-col items-center gap-2 text-sm text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin" /> Preparing secure form…
            </div>
          )}
          {setup && stripePromise && !preparing && (
            <Elements stripe={stripePromise} options={{ clientSecret: setup.client_secret }}>
              <AddCardForm
                basePath={basePath}
                clientSecret={setup.client_secret}
                makePrimary={hasNoCards}
                onDone={() => { setAddOpen(false); setSetup(null); refresh(); }}
                onCancel={() => { setAddOpen(false); setSetup(null); }}
              />
            </Elements>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
